import styled from "styled-components";
import { useCallback, useEffect, useState, useRef } from "react";
import { gql, useMutation } from "@apollo/client";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { captureException } from "@sentry/react";

import { AWS_USER_UPLOAD_RESIZED_URL, AWS_PROFILE_IMAGES_URL } from "constants";
import { POST_VIEW_TIME } from "constants";
import Discover from "./Discover";
import { useNavigate } from "react-router-dom";

// Actions
import { updateProgressViews, updateStreak } from "store/actions";
import { getDBShard } from "modules/functions";

const PreloadedImage = styled.div`
  position: fixed;
  bottom: 0;
  z-index: -1000;
  opacity: 1;
  width: 1px;
  height: 1px;

  pointer-events: none;
`;

export default () => {
  const [key, setKey] = useState(1);

  const remountRequest = () => {
    setKey((prevKey) => {
      return prevKey + 1;
    });
  };

  return <DiscoverContainer key={key} remountRequest={remountRequest} />;
};

const DiscoverContainer = ({ remountRequest }) => {
  const container = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [impression, setImpression] = useState({
    shardNumber: null,
    impressionId: null,
    post: null,
    probability: null,
    reward: null,
    streak: null,
  });
  const [waitingImpression, setWaitingImpression] = useState({
    shardNumber: null,
    impressionId: null,
    post: null,
    probability: null,
    reward: null,
    streak: null,
    postLiked: false,
    likes: 0,
  });
  //const [status, setStatus] = useState(null);
  const [counter, setCounter] = useState(0);
  const [nextButton, setNextButton] = useState(false);
  const [probabilityBox, setProbabilityBox] = useState(false);
  const [noPost, setNoPost] = useState(false);
  const [requestChatLoading, setRequestChatLoading] = useState(false);
  const [postLiked, setPostLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [interacted, setInteracted] = useState(false);
  const [firstView, setFirstView] = useState(true);
  const [reportMenuOpened, setReportMenuOpened] = useState(false);
  const [postReported, setPostReported] = useState(false);
  const [sponsoredMessageImpression, setSponsoredMessageImpression] = useState({
    sponsoredMessageImpressionId: null,
    sponsoredMessage: null,
  });
  const [fastStartModal, setFastStartModal] = useState(false);
  const [challenge, setChallenge] = useState(false);

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
      hasViews: user.hasViews,
    }),
  );

  const { profileData, hasViews } = useSelector(UserProperties);

  // Prepare Mutations
  const REQUEST_IMPRESSION = gql`
    mutation RequestImpression {
      requestImpression {
        impression {
          id
          #   requested_at
        }
        post {
          id
          created_at
          text
          image
          image_link
          video
          user {
            id
            username
            profile_image
            highlighted_posts
            ad_plan
            show_checkmark
          }
        }
        shardNumber
        probability
        reward
        postLiked
        likes
        postReported
        sponsoredMessage {
          text
          link_text
          link
          user {
            id
            username
            profile_image
            ad_plan
          }
        }
        sponsoredMessageImpression {
          id
        }
        streak
        challengeLock
      }
    }
  `;

  const [requestImpression] = useMutation(REQUEST_IMPRESSION);

  const REGISTER_IMPRESSION = gql`
    mutation RegisterImpression(
      $impressionId: ID!
      $shardNumber: Int!
      $firstView: Boolean!
    ) {
      registerImpression(
        impressionId: $impressionId
        shardNumber: $shardNumber
        firstView: $firstView
      ) {
        status
      }
    }
  `;

  const [registerImpression] = useMutation(REGISTER_IMPRESSION);

  // remove includeChallenge variable later when all have updated frontend
  const REGISTER_REWARD = gql`
    mutation RequestReward($includeChallenge: Boolean) {
      registerReward(includeChallenge: $includeChallenge) {
        status
      }
    }
  `;

  const [registerReward] = useMutation(REGISTER_REWARD);

  const REGISTER_INTERACTION = gql`
    mutation RegisterInteraction($impressionId: ID!, $shardNumber: Int!) {
      registerInteraction(
        impressionId: $impressionId
        shardNumber: $shardNumber
      ) {
        status
      }
    }
  `;

  const [registerInteraction] = useMutation(REGISTER_INTERACTION);

  const LIKE_POST = gql`
    mutation LikePost($postId: ID!, $shardNumber: Int!) {
      likePost(postId: $postId, shardNumber: $shardNumber) {
        status
      }
    }
  `;

  const [likePost] = useMutation(LIKE_POST);

  const REPORT_POST = gql`
    mutation ReportPost($postId: ID!, $shardNumber: Int!, $reason: String!) {
      reportPost(postId: $postId, shardNumber: $shardNumber, reason: $reason) {
        status
      }
    }
  `;

  const [reportPost] = useMutation(REPORT_POST);

  // Request impression function
  const sendRequest = async () => {
    try {
      //setStatus("requestSent");

      const result = await requestImpression();

      if (result.data.requestImpression.sponsoredMessage) {
        setSponsoredMessageImpression({
          sponsoredMessageImpressionId:
            result.data.requestImpression.sponsoredMessageImpression.id,
          sponsoredMessage: result.data.requestImpression.sponsoredMessage,
        });
      }

      return result.data.requestImpression;
    } catch (error) {
      captureException(error);
      console.log("Error Request impression", error);
    }
  };

  // Get first post on load
  useEffect(() => {
    const getFirstPost = async () => {
      const impression = await sendRequest();
      //console.log("impression", impression);

      if (impression?.impression) {
        setImpression({
          shardNumber: impression.shardNumber,
          impressionId: impression.impression.id,
          post: impression.post,
          probability: impression.probability,
        });

        setPostLiked(impression.postLiked);
        setLikes(impression.likes);
        setPostReported(impression.postReported);

        //setStatus("counting");
        setProbabilityBox(true);
      } else if (impression?.reward) {
        setImpression({
          shardNumber: null,
          impressionId: null,
          post: null,
          probability: null,
          reward: impression.reward,
          streak: impression.streak,
        });

        setPostLiked(false);
        setLikes(0);
        setPostReported(false);

        //setStatus("waiting");
      } else {
        // No post returned
        setNoPost(true);
      }
    };

    getFirstPost();
  }, []);

  // Start counter on Impression change
  useEffect(() => {
    if (impression.impressionId) {
      setCounter(0);

      const interval = setInterval(() => {
        setCounter((prevCounter) => {
          const newCounter = prevCounter + 1;
          if (newCounter >= POST_VIEW_TIME) clearInterval(interval);
          return newCounter;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [impression]);

  const sendRewardRegistration = async () => {
    try {
      //setStatus("registerSent");

      //const result = await registerReward();

      const result = await registerReward({
        variables: {
          includeChallenge: true,
        },
      });

      if (result.data.registerReward.status === "Registered") {
        const impression = await sendRequest();

        if (impression?.impression) {
          setWaitingImpression({
            shardNumber: impression.shardNumber,
            impressionId: impression.impression.id,
            post: impression.post,
            probability: impression.probability,
            reward: impression.reward,
            streak: impression.streak,
            postLiked: impression.postLiked,
            likes: impression.likes,
          });

          //setStatus("waiting");
        } else {
          // No post returned
          setNoPost(true);
        }

        if (impression?.challengeLock) {
          setChallenge(true);
        } else {
          setChallenge(false);
        }
      }
    } catch (error) {
      captureException(error);
      console.log("Error Register reward", error);
    }
  };

  // Register Reward and request new Impression function
  useEffect(() => {
    if (impression.reward) {
      sendRewardRegistration();
    }
  }, [impression]);

  // Register impression and request new one function
  const sendRegistration = async () => {
    try {
      //console.log("sendRegistration");

      const result = await registerImpression({
        variables: {
          impressionId: impression.impressionId,
          shardNumber: impression.shardNumber,
          firstView,
        },
      });

      if (result.data.registerImpression.status === "Registered") {
        setFirstView(false);

        const impression = await sendRequest();
        //console.log("impression",impression)

        if (impression?.impression) {
          setWaitingImpression({
            shardNumber: impression.shardNumber,
            impressionId: impression.impression.id,
            post: impression.post,
            probability: impression.probability,
            reward: impression.reward,
            streak: impression.streak,
            postLiked: impression.postLiked,
            likes: impression.likes,
            postReported: impression.postReported,
          });

          //setStatus("waiting");
        } else if (impression.reward) {
          setWaitingImpression({
            shardNumber: null,
            impressionId: null,
            post: null,
            probability: null,
            postLiked: false,
            likes: 0,
            reward: impression.reward,
            streak: impression.streak,
            postReported: false,
          });

          //setStatus("waiting");
        } else {
          // No post returned
          setNoPost(true);
        }

        if (impression?.challengeLock) {
          setChallenge(true);
        } else {
          setChallenge(false);
        }

        if (!hasViews) {
          dispatch(updateProgressViews(true));
        }
      }
    } catch (error) {
      captureException(error);
      console.log("Error Register impression", error);
    }
  };

  // Register impression when it's time
  useEffect(() => {
    const advance = 4; // advance time to send impression registration

    // check if tab is visible first
    if (counter === POST_VIEW_TIME - advance - 1) {
      if (!isTabVisible && !interacted) {
        setCounter(0);
      }
    }

    if (counter === POST_VIEW_TIME - advance) {
      sendRegistration();
    }

    if (counter === POST_VIEW_TIME - 1) {
      setProbabilityBox(false);
    }
  }, [counter]);

  // Show Next button when post time finished and waiting post ready
  useEffect(() => {
    if (
      counter >= POST_VIEW_TIME &&
      (waitingImpression.impressionId || waitingImpression.reward || noPost)
    ) {
      setNextButton(true);
    }

    // Show button when Reward found
    if (impression.reward && (waitingImpression.impressionId || noPost)) {
      setNextButton(true);
    }
  }, [counter, waitingImpression, noPost]);

  // handle Post change
  const switchPost = () => {
    setImpression(waitingImpression);

    setPostLiked(waitingImpression.postLiked);
    setLikes(waitingImpression.likes);
    setInteracted(false);
    setPostReported(waitingImpression.postReported);

    setWaitingImpression({
      shardNumber: null,
      impressionId: null,
      post: null,
      probability: null,
      reward: null,
      streak: null,
      postLiked: false,
      likes: 0,
      postReported: false,
    });

    //setStatus("counting");
  };

  useEffect(() => {
    if (impression.streak) {
      // update streak in store
      dispatch(updateStreak(impression.streak));
    }
  }, [impression]);

  gsap.registerPlugin(useGSAP);
  const { contextSafe } = useGSAP({ scope: container });

  // Show next Post click
  const showNextPost = contextSafe(() => {
    const nextHighlighted = waitingImpression.post?.user.highlighted_posts;
    //console.log({ nextHighlighted });

    if (noPost) remountRequest();

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 600);

    const postTl = gsap.timeline({ paused: true });
    postTl.to("#postBox", {
      zIndex: 1,
      duration: 0,
    });
    postTl.to("#postBox", {
      y: -window.innerHeight,
      duration: 0.35,
      ease: "power1.inOut",
    });

    if (!nextHighlighted) {
      postTl.to("#postBox", {
        background: "linear-gradient(138deg, #2B4055 1.65%, #1A2733 63.37%)",
        boxShadow: "none",
        duration: 0,
      });
    }

    if (nextHighlighted) {
      postTl.to("#postBox", {
        background:
          "linear-gradient(138deg,rgb(69, 93, 117) 1.65%, #314151 63.37%)",
        boxShadow: "0px 0px 15px 1px rgba(238, 229, 233, 0.5)",
        duration: 0,
      });
    }

    postTl.call(switchPost);
    postTl.to("#postBox", {
      y: 0,
      duration: 0.35,
      ease: "power1.inOut",
      delay: 0,
    });
    postTl.to("#postBox", {
      zIndex: 11,
      duration: 0,
    });

    postTl.play();

    setNextButton(false);
    setProbabilityBox(true);
  });

  // control Next button
  const showNextButton = contextSafe(() => {
    const buttonTl = gsap.timeline({ paused: true });
    buttonTl.to("#nextButton", { display: "block", duration: 0 });
    buttonTl.to("#nextButton", {
      opacity: 1,
      duration: 0.15,
    });

    buttonTl.play();
  });

  const hideNextButton = contextSafe(() => {
    const buttonTl = gsap.timeline({ paused: true });
    buttonTl.to("#nextButton", {
      opacity: 0,
      duration: 0.15,
    });
    buttonTl.to("#nextButton", { display: "none", duration: 0 });

    buttonTl.play();
  });

  useEffect(() => {
    if (nextButton) {
      showNextButton();
    }

    if (!nextButton) {
      hideNextButton();
    }
  }, [nextButton]);

  // control Coins probability box
  const showProbabilityBox = contextSafe(() => {
    const probabilityTl = gsap.timeline({ paused: true });
    probabilityTl.to("#probabilityBox", {
      opacity: 1,
      duration: 0,
      delay: 1.5,
    });
    probabilityTl.to("#probabilityBox", { y: 0 });

    probabilityTl.play();
  });

  const hideProbabilityBox = contextSafe(() => {
    const probabilityTl = gsap.timeline({ paused: true });
    probabilityTl.to("#probabilityBox", { y: -180, delay: 0.3 });
    probabilityTl.to("#probabilityBox", { opacity: 0, duration: 0 });

    probabilityTl.play();
  });

  useEffect(() => {
    if (probabilityBox && !impression.reward && !waitingImpression.reward) {
      showProbabilityBox();
    }

    if (!probabilityBox) {
      hideProbabilityBox();
    }
  }, [probabilityBox]);

  // // preload Post image
  // useEffect(() => {
  //   if (waitingImpression.post?.image) {
  //     const img = new Image();
  //     img.src = waitingImpression.post.image;
  //   }
  // }, [waitingImpression]);

  // handle Interactions
  const linkClicked = async (e) => {
    setInteracted(true);

    try {
      await registerInteraction({
        variables: {
          impressionId: impression.impressionId,
          shardNumber: impression.shardNumber,
        },
      });
    } catch (error) {
      captureException(error);
      console.log("linkClicked error", error);
    }
  };

  // handle New Message click
  const REQUEST_CHAT = gql`
    mutation RequestChat($partnerId: ID!) {
      requestChat(partnerId: $partnerId) {
        id
      }
    }
  `;

  const [requestChat] = useMutation(REQUEST_CHAT);

  const onNewMessageClick = async () => {
    try {
      if (requestChatLoading) return;

      setRequestChatLoading(true);

      const result = await requestChat({
        variables: {
          partnerId: impression.post.user.id,
        },
      });

      if (!result.data.requestChat) {
        navigate(
          `/createchat?postid=${impression.post.id}&sn=${impression.shardNumber}`,
        );
      } else {
        navigate(`/chat?id=${result.data.requestChat.id}`);
      }

      setRequestChatLoading(false);
    } catch (error) {
      captureException(error);
      setRequestChatLoading(false);
      console.log("requestChat error", error);
    }
  };

  // handle Like post
  const likePostClick = async () => {
    try {
      if (postLiked) return;

      setPostLiked(true);
      setLikes((prevState) => prevState + 1);

      await likePost({
        variables: {
          postId: impression.post.id,
          shardNumber: impression.shardNumber,
        },
      });
    } catch (error) {
      captureException(error);
      console.log("likePostClick error", error);
    }
  };

  const counterWidth = (100 / POST_VIEW_TIME) * counter;

  const handleVisibilityChange = useCallback(() => {
    setIsTabVisible(document.visibilityState === "visible");
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // handle Report post
  const reportPostMenuClick = () => {
    if (postReported) return;
    setReportMenuOpened((prevState) => !prevState);
  };

  const closeReportMenu = () => {
    setReportMenuOpened(false);
  };

  const onReportPost = async (reason) => {
    try {
      if (postReported) return;

      setPostReported(true);
      setReportMenuOpened(false);

      await reportPost({
        variables: {
          postId: impression.post.id,
          shardNumber: impression.shardNumber,
          reason,
        },
      });
    } catch (error) {
      captureException(error);
      console.log("likePostClick error", error);
    }
  };

  // handle Sponsored message click
  const REGISTER_SPONSORED_MESSAGE_CLICK = gql`
    mutation RegisterSponsoredMessageClick(
      $impressionId: ID!
      $shardNumber: Int!
    ) {
      registerSponsoredMessageClick(
        impressionId: $impressionId
        shardNumber: $shardNumber
      ) {
        resultCode
      }
    }
  `;

  const [registerSponsoredMessageClick] = useMutation(
    REGISTER_SPONSORED_MESSAGE_CLICK,
  );

  const onSponsoredMessageClick = async () => {
    try {
      const shardNumber = getDBShard(
        sponsoredMessageImpression.sponsoredMessage.user.id,
        true,
      );

      await registerSponsoredMessageClick({
        variables: {
          impressionId: sponsoredMessageImpression.sponsoredMessageImpressionId,
          shardNumber,
        },
      });
    } catch (error) {
      captureException(error);
      console.log("onSponsoredMessageClick error", error);
    }
  };

  // handle Fast Start Bonus modal
  useEffect(() => {
    const alreadyShowedFastStartModal =
      localStorage.getItem("closed-fast-start-modal") === "y";

    if (profileData.fastStartBonus && !alreadyShowedFastStartModal) {
      const now = new Date();
      const bonusExpires = new Date(profileData.fastStartBonus);

      if (bonusExpires.getTime() > now.getTime()) {
        setFastStartModal(true);
      }
    }
  }, [profileData]);

  const closeFastStartModal = () => {
    setFastStartModal(false);
    localStorage.setItem("closed-fast-start-modal", "y");
  };

  // console.log("waitingImpression", waitingImpression);
  //console.log("impression", impression);

  return (
    <>
      <Discover
        post={impression.post}
        counterWidth={counterWidth}
        showNextPost={showNextPost}
        container={container}
        probability={impression.probability}
        reward={impression.reward}
        streak={impression.streak}
        tryAgain={!impression.impressionId && !impression.reward && noPost}
        onTryAgainClick={remountRequest}
        linkClicked={linkClicked}
        onNewMessageClick={onNewMessageClick}
        likePostClick={likePostClick}
        postLiked={postLiked}
        likes={likes}
        reportMenuOpened={reportMenuOpened}
        closeReportMenu={closeReportMenu}
        reportPostMenuClick={reportPostMenuClick}
        onReportPost={onReportPost}
        postReported={postReported}
        imageToPreload={
          waitingImpression.post ? waitingImpression.post.image : null
        }
        initialHighlighted={impression.post?.user.highlighted_posts}
        sponsoredMessage={sponsoredMessageImpression.sponsoredMessage}
        onSponsoredMessageClick={onSponsoredMessageClick}
        fastStartModal={fastStartModal}
        closeFastStartModal={closeFastStartModal}
        challenge={challenge}
      />
      <PreloadedImage>
        {waitingImpression.post && (
          <img
            src={AWS_USER_UPLOAD_RESIZED_URL + waitingImpression.post.image}
          />
        )}
      </PreloadedImage>
      <PreloadedImage>
        {waitingImpression.post && (
          <img
            src={
              AWS_PROFILE_IMAGES_URL + waitingImpression.post.user.profile_image
            }
          />
        )}
      </PreloadedImage>
    </>
  );
};
